// Example
// https://github.com/vuejs/vuex/tree/main/examples/composition/shopping-cart

import { login, reset, refreshLogin } from '@/api/login.js'
import { PostMessageExternal } from '@/api/message.js'
import { clearSessionStorage } from '@/utils/preferences.js'
import router from '@/router'
// import useEventsBus from '../../eventsBus/eventsBus.js';
import { useToast } from 'vue-toastification'
import i18n from '../../locales/i18n'

import ReLoginToast from '@/components/subcomponents/ReLoginToast.vue'

const toast = useToast()

const state = () => ({
  username: '',
  password: '',
  offlineMessage: '',
  offlineMessageEmail: '',
  offlineMessageSubject: '',
  offlineMessageName: '',
  offlineMessagePhone: '',
  offlineMessageCompany: '',
  token: '',
  recaptchaToken: '',
  refreshToken: '',
  type: 'SignIn',
  counter: false,
  timerWarning: true,
  relogin: false,
  secondsToLogout: 0,
  lastActive: 0,
  lastTokenFetch: 0
})

// getters
const getters = {
  getUsername (state) {
    return state.username
  },
  getPassword (state) {
    return state.password
  },
  getToken (state) {
    return state.token
  }

}

// actions
const actions = {

  async handleLoginSubmit ({ state, commit, dispatch }) {
    try {
      const response = await login(state.username, state.password)

      // commit('clearPassword')
      // Set token in localstorage for easy access
      localStorage.setItem('token', response.data.access_token)
      localStorage.setItem('refreshToken', response.data.refresh_token)

      const tokenExpiryDate = new Date()
      tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + parseInt(response.data.expires_in) - 10)

      dispatch('resetCounter')

      const ticks = ((tokenExpiryDate.getTime() * 10000) + 621355968000000000)
      localStorage.setItem('tokenExpiryDateInTicks', ticks)

      // set token in state
      commit('setToken', response.data.access_token)
      commit('setRefreshToken', response.data.refresh_token)

      commit('setLastTokenFetch', new Date().getTime())

      localStorage.setItem('username', state.username)

      try {
        const user = await dispatch('account/getMe', null, { root: true })
        if (!user || typeof user.Phone !== 'string' || !user.Phone.trim()) {
          commit('account/showContactInformationPrompt', null, { root: true })
        }
      } catch (e) {
        console.log('Could not check the user contact information', e)
      }
    } catch (e) {
      const messageKey = e.response?.status === 400 && e.response?.data?.error === 'invalid_grant'
        ? 'loginPage.invalidCredentials'
        : 'loginPage.loginFailed'
      toast.warning(i18n.global.t(messageKey))
    }
    commit('clearPassword')
    if (localStorage.getItem('token')) {
      dispatch('startCounter')

      const attemptedRoute = localStorage.getItem('attemptedRoute')
      if (attemptedRoute) {
        localStorage.removeItem('attemptedRoute') // Remove attempted route after redirection
        router.push(attemptedRoute)
      } else {
        // If no attempted route, redirect to default dashboard
        router.push('/dashboard')
      }
    } else {
      clearSessionStorage()
    }
  },
  async sendExternalMessage ({ state, commit, dispatch }) {
    if (!state.offlineMessageName || !state.offlineMessagePhone || !state.offlineMessageEmail || !state.offlineMessageCompany || !state.offlineMessageSubject || !state.offlineMessage) {
      toast.warning(i18n.global.t('loginPage.fillInAllFields'))
      return
    }
    if (!(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(state.offlineMessageEmail))) {
      toast.warning(i18n.global.t('loginPage.invalidEmailAddress'))
      return false
    }
    if (!state.recaptchaToken) {
      toast.warning(i18n.global.t('loginPage.completeRecaptchaToContinue'))
      return
    }
    try {
      const json =
      {
        Name: state.offlineMessageName,
        Phone: state.offlineMessagePhone,
        Email: state.offlineMessageEmail,
        Company: state.offlineMessageCompany,
        Subject: state.offlineMessageSubject,
        Message: state.offlineMessage,
        Recaptcha: state.recaptchaToken
      }
      const response = await PostMessageExternal(json)
      if (response.status === 200) {
        commit('setType', 'MessageSent')
        commit('setOfflineMessageName', '')
        commit('setOfflineMessagePhone', '')
        commit('setOfflineMessageEmail', '')
        commit('setOfflineMessageCompany', '')
        commit('setOfflineMessageSubject', '')
        commit('setOfflineMessage', '')
        commit('setRecaptchaToken', '')
        toast.success(i18n.global.t('loginPage.messageSent'))
      }
    } catch (e) {
      toast.warning(i18n.global.t('loginPage.messageSendingFailed') + '\r\n\r\n' + e.message)
    }
  },

  async reLogin ({ state, commit, dispatch }) {
    try {
      const response = await refreshLogin(state.refreshToken)
      const tokenExpiryDate = new Date()
      tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + parseInt(response.data.expires_in) - 10)
      const currentDate = new Date()
      const currentTick = currentDate.getTime() * 10000 + 621355968000000000
      const ticks = ((tokenExpiryDate.getTime() * 10000) + 621355968000000000)
      localStorage.setItem('token', response.data.access_token)
      localStorage.setItem('refreshToken', response.data.refresh_token)
      localStorage.setItem('tokenExpiryDateInTicks', ticks)
      localStorage.setItem('lastTokenRefreshTick', currentTick)

      commit('setToken', response.data.access_token)
      commit('setLastTokenFetch', currentDate.getTime())
      commit('setRefreshToken', response.data.refresh_token)
      dispatch('startCounter')
    } catch (e) {
      toast.warning(i18n.global.t('loginPage.loginFailed'))
      router.push('/login')
    }
  },

  handleForgotPassword ({ state, commit }) {
    if (state.type === 'SignIn') {
      commit('setType', 'ForgotPassword')
    } else {
      commit('setType', 'SignIn')
    }
  },

  handleMessageSending ({ state, commit }) {
    if (state.type !== 'MessageSending') {
      commit('setType', 'MessageSending')
    } else {
      commit('setType', 'SignIn')
    }
  },

  resetPassword ({ state, commit }) {
    const json = {}
    json.Email = state.username
    try {
      reset(json)
      toast.success(i18n.global.t('loginPage.resetPasswordSuccess'))
      commit('setType', 'SignIn')
    } catch (e) {
      toast.warning(i18n.global.t('loginPage.resetPasswordFailed') + '\r\n\r\n' + e.message)
    }
  },

  startCounterOnRefresh ({ state, commit, dispatch }) {
    commit('setRefreshToken', localStorage.getItem('refreshToken'))
    if (!state.counter) {
      dispatch('startCounter')
    }
  },
  /* TIMER LOGIC */
  async startCounter ({ state, commit, dispatch }) {
    commit('setCounter', true)
    let toastId = -1
    while (state.counter) {
      const currentDate = new Date()
      const currentTick = currentDate.getTime() * 10000 + 621355968000000000
      const expiryTick = parseInt(localStorage.getItem('tokenExpiryDateInTicks'))
      const secondsBetweenNowAndExpiryDate = (expiryTick - currentTick) / 10000 / 1000 - 5
      commit('setSecondsToLogout', secondsBetweenNowAndExpiryDate)
      if (currentTick > expiryTick || secondsBetweenNowAndExpiryDate < 0) {
        break
      }

      // Toast relogin at 5 minutes remaining
      if (secondsBetweenNowAndExpiryDate < 300 && state.timerWarning) {
        commit('setTimerWarning', false)

        const reloginComponent = {
          component: ReLoginToast,
          props: {
            timeToLogout: secondsBetweenNowAndExpiryDate
          },
          listeners: {
            reloginClick: () => {
              dispatch('resetCounter')
              commit('reLogin', true)
            }
          },
          icon: {
            iconClass: 'logout',
            iconChildren: '',
            iconTag: 'i'
          }
        }
        toastId = toast.info(reloginComponent, {
          timeout: 0,
          closeOnClick: false,
          icon: {
            iconClass: 'material-icons',
            iconChildren: 'logout',
            iconTag: 'span'
          }
        })
      }

      if (toastId !== -1) {
        const reloginComponent = {
          component: ReLoginToast,
          props: {
            timeToLogout: secondsBetweenNowAndExpiryDate
          },
          listeners: {
            reloginClick: () => {
              dispatch('resetCounter')
              commit('reLogin', true)
            }
          }
        }
        toast.update(toastId, { content: reloginComponent })
      }
      await new Promise(resolve => setTimeout(resolve, 1000))
    }
    // reset counter
    if (state.counter) {
      dispatch('resetCounter')

      // Handle logout
      clearSessionStorage()
      dispatch('sidebar/sidebarLogOut', '', { root: true })
      location.reload()
    }
    if (state.relogin) {
      dispatch('reLogin')
      commit('reLogin', false)
    }
  },

  resetCounter ({ state, commit }) {
    commit('setCounter', false)
    commit('setTimerWarning', true)
  },

  resetCounterAndReLogin ({ state, commit }) {
    commit('setCounter', false)
    commit('setTimerWarning', true)
  },

  setLastActiveTick ({ state, commit }) {
    const currentDate = new Date()
    const currentTick = currentDate.getTime()
    commit('setLastActive', currentTick)
  }

}

// mutations
const mutations = {
  setUsername (state, username) {
    state.username = username
  },
  setPassword (state, password) {
    state.password = password
  },
  setOfflineMessageEmail (state, offlineMessageEmail) {
    state.offlineMessageEmail = offlineMessageEmail
  },
  setOfflineMessage (state, offlineMessage) {
    state.offlineMessage = offlineMessage
  },
  setOfflineMessageName (state, offlineMessageName) {
    state.offlineMessageName = offlineMessageName
  },
  setOfflineMessagePhone (state, offlineMessagePhone) {
    state.offlineMessagePhone = offlineMessagePhone
  },
  setOfflineMessageCompany (state, offlineMessageCompany) {
    state.offlineMessageCompany = offlineMessageCompany
  },
  setOfflineMessageSubject (state, offlineMessageSubject) {
    state.offlineMessageSubject = offlineMessageSubject
  },
  clearPassword (state) {
    state.password = ''
  },
  setToken (state, token) {
    state.token = token
  },
  setLastTokenFetch (state, value) {
    state.lastTokenFetch = value
  },
  setRefreshToken (state, refreshToken) {
    state.refreshToken = refreshToken
  },
  setType (state, type) {
    state.type = type
  },
  setCounter (state, value) {
    state.counter = value
  },
  setTimerWarning (state, value) {
    state.timerWarning = value
  },
  reLogin (state, value) {
    state.relogin = value
  },
  setSecondsToLogout (state, value) {
    state.secondsToLogout = value
  },
  setLastActive (state, value) {
    state.lastActive = value
  },
  setRecaptchaToken (state, value) {
    state.recaptchaToken = value
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
